import { useState } from 'react';
import { X, CheckCircle, AlertCircle } from 'lucide-react';
import { type Job } from '../data/jobs';

interface JobApplicationFormProps {
  job: Job;
  isOpen: boolean;
  onClose: () => void;
}

function JobApplicationFormContent({ job, isOpen, onClose }: JobApplicationFormProps) {
  const APPLICATION_EMAIL = 'careers@noemares.com';
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    linkedin_url: '',
    github_url: '',
    portfolio_url: '',
    cover_letter: '',
    resume_url: '',
    years_experience: 0,
    location: '',
    availability: '',
    why_volunteer: '',
    skills: [] as string[]
  });
  
  const [skillInput, setSkillInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const steps = [
    { id: 'welcome', title: "Let's get started!" },
    { id: 'personal', title: `Nice to meet you, ${formData.full_name || 'there'}!` },
    { id: 'experience', title: `Tell us about your experience, ${formData.full_name}` },
    { id: 'social', title: 'Share your online presence' },
    { id: 'motivation', title: 'Why do you want to join us?' },
    { id: 'review', title: 'Review your application' }
  ];

  const totalSteps = steps.length;

  if (!isOpen) return null;

  const nextStep = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return formData.full_name.trim().length >= 2; // Name
      case 1: return formData.email.trim() && formData.email.includes('@'); // Contact
      case 2: return formData.years_experience > 0 && formData.skills.length > 0 && formData.location.trim() && formData.availability; // Experience
      case 3: return true; // Links (all optional)
      case 4: return formData.cover_letter.trim() && formData.why_volunteer.trim(); // Motivation
      case 5: return true; // Review
      default: return false;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'years_experience' ? parseInt(value) || 0 : value
    }));
  };

  const addSkill = () => {
    if (skillInput.trim() && !formData.skills.includes(skillInput.trim())) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, skillInput.trim()]
      }));
      setSkillInput('');
    }
  };

  const removeSkill = (skillToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill !== skillToRemove)
    }));
  };

  const buildMailtoLink = () => {
    const subject = encodeURIComponent(`Application for ${job.title}`);
    const lines = [
      `Role: ${job.title}`,
      `Name: ${formData.full_name}`,
      `Email: ${formData.email}`,
      `Phone: ${formData.phone || 'Not provided'}`,
      `LinkedIn: ${formData.linkedin_url || 'Not provided'}`,
      `GitHub: ${formData.github_url || 'Not provided'}`,
      `Portfolio: ${formData.portfolio_url || 'Not provided'}`,
      `Resume: ${formData.resume_url || 'Not provided'}`,
      `Years of Experience: ${formData.years_experience || 'Not provided'}`,
      `Location: ${formData.location || 'Not provided'}`,
      `Availability: ${formData.availability || 'Not provided'}`,
      `Skills: ${formData.skills.length ? formData.skills.join(', ') : 'Not provided'}`,
      '',
      'Cover Letter:',
      formData.cover_letter || 'Not provided',
      '',
      'Why volunteer with us?:',
      formData.why_volunteer || 'Not provided'
    ];

    const body = encodeURIComponent(lines.join('\n'));
    return `mailto:${APPLICATION_EMAIL}?subject=${subject}&body=${body}`;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const mailtoLink = buildMailtoLink();
      window.location.href = mailtoLink;

      setSubmitStatus('success');
      setTimeout(() => {
        onClose();
        setCurrentStep(0);
        setFormData({
          full_name: '',
          email: '',
          phone: '',
          linkedin_url: '',
          github_url: '',
          portfolio_url: '',
          cover_letter: '',
          resume_url: '',
          years_experience: 0,
          location: '',
          availability: '',
          why_volunteer: '',
          skills: []
        });
        setSkillInput('');
        setSubmitStatus('idle');
      }, 2000);
    } catch (error) {
      console.error('Error preparing email:', error);
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="text-center space-y-6">
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                What's your name?
              </h3>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                placeholder="Enter your full name"
                className="w-full px-6 py-4 text-lg rounded-2xl border border-gray-300 dark:border-dark-400 bg-white dark:bg-dark-300 focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white text-center"
                autoFocus
              />
            </div>
          </div>
        );
      
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Hey {formData.full_name}! 👋
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Let's gather some basic information to get started
              </p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your.email@example.com"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-dark-400 bg-white dark:bg-dark-300 focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Phone Number (Optional)
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+1 (555) 123-4567"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-dark-400 bg-white dark:bg-dark-300 focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Tell us about your experience 💼
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Help us understand your background and skills
              </p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Years of Experience *
                </label>
                <select
                  name="years_experience"
                  value={formData.years_experience}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-dark-400 bg-white dark:bg-dark-300 focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white"
                >
                  <option value="">Select experience level</option>
                  <option value="0-1">0-1 years (Entry Level)</option>
                  <option value="2-5">2-5 years (Mid Level)</option>
                  <option value="6-10">6-10 years (Senior Level)</option>
                  <option value="10+">10+ years (Expert Level)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Skills & Technologies *
                </label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={skillInput}
                      onChange={(e) => setSkillInput(e.target.value)}
                      placeholder="Enter a skill..."
                      className="flex-1 px-4 py-3 rounded-xl border border-gray-300 dark:border-dark-400 bg-white dark:bg-dark-300 focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                    />
                    <button
                      type="button"
                      onClick={addSkill}
                      className="px-4 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
                    >
                      Add
                    </button>
                  </div>
                  {formData.skills.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.skills.map((skill, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 dark:bg-primary-800 text-primary-800 dark:text-primary-200 rounded-full text-sm"
                        >
                          {skill}
                          <button
                            type="button"
                            onClick={() => removeSkill(skill)}
                            className="ml-1 hover:bg-primary-200 dark:hover:bg-primary-700 rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Location
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="City, Country or Timezone"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-dark-400 bg-white dark:bg-dark-300 focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Availability
                </label>
                <select
                  name="availability"
                  value={formData.availability}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-dark-400 bg-white dark:bg-dark-300 focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white"
                >
                  <option value="">Select availability</option>
                  <option value="Part-time (5-10 hours/week)">Part-time (5-10 hours/week)</option>
                  <option value="Part-time (10-20 hours/week)">Part-time (10-20 hours/week)</option>
                  <option value="Full-time (20+ hours/week)">Full-time (20+ hours/week)</option>
                  <option value="Flexible">Flexible</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Share your online presence 🌐
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Help us learn more about your work (all optional)
              </p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  LinkedIn Profile
                </label>
                <input
                  type="url"
                  name="linkedin_url"
                  value={formData.linkedin_url}
                  onChange={handleInputChange}
                  placeholder="https://linkedin.com/in/yourprofile"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-dark-400 bg-white dark:bg-dark-300 focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  GitHub Profile
                </label>
                <input
                  type="url"
                  name="github_url"
                  value={formData.github_url}
                  onChange={handleInputChange}
                  placeholder="https://github.com/yourusername"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-dark-400 bg-white dark:bg-dark-300 focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Portfolio / Website
                </label>
                <input
                  type="url"
                  name="portfolio_url"
                  value={formData.portfolio_url}
                  onChange={handleInputChange}
                  placeholder="https://yourportfolio.com"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-dark-400 bg-white dark:bg-dark-300 focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Resume/CV Link
                </label>
                <input
                  type="url"
                  name="resume_url"
                  value={formData.resume_url}
                  onChange={handleInputChange}
                  placeholder="Link to your resume (Google Drive, Dropbox, etc.)"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-dark-400 bg-white dark:bg-dark-300 focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Why do you want to join us? ✨
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Tell us about your motivation and background
              </p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Cover Letter *
                </label>
                <textarea
                  name="cover_letter"
                  value={formData.cover_letter}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Tell us about your background, relevant experience, and what interests you about this role..."
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-dark-400 bg-white dark:bg-dark-300 focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Why volunteer with us? *
                </label>
                <textarea
                  name="why_volunteer"
                  value={formData.why_volunteer}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="What motivates you to volunteer with our team? What do you hope to learn or contribute?"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-dark-400 bg-white dark:bg-dark-300 focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Almost done! 🎉
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Review your application before submitting
              </p>
            </div>
            <div className="space-y-4 bg-gray-50 dark:bg-dark-400 rounded-2xl p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-semibold text-gray-700 dark:text-gray-300">Name:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{formData.full_name}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700 dark:text-gray-300">Email:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{formData.email}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700 dark:text-gray-300">Experience:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{formData.years_experience}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700 dark:text-gray-300">Availability:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{formData.availability}</span>
                </div>
                <div className="md:col-span-2">
                  <span className="font-semibold text-gray-700 dark:text-gray-300">Location:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{formData.location}</span>
                </div>
                {formData.skills.length > 0 && (
                  <div className="md:col-span-2">
                    <span className="font-semibold text-gray-700 dark:text-gray-300">Skills:</span>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {formData.skills.map((skill, index) => (
                        <span key={index} className="px-2 py-1 bg-primary-100 dark:bg-primary-800 text-primary-800 dark:text-primary-200 rounded-full text-xs">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl mx-auto rounded-3xl shadow-2xl bg-white dark:bg-dark-200 border border-gray-200 dark:border-dark-400 overflow-hidden">
        
        {/* Progress Bar */}
        <div className="h-2 bg-gray-200 dark:bg-dark-400">
          <div 
            className="h-full bg-gradient-to-r from-primary-500 to-primary-600 transition-all duration-300"
            style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
          />
        </div>

        {/* Close Button */}
        <div className="absolute top-4 right-4 z-10">
          <button
            onClick={onClose}
            className="p-2 bg-gray-100 dark:bg-dark-300 rounded-full hover:bg-gray-200 dark:hover:bg-dark-400 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
        </div>

        <div className="px-8 py-10">
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">
              {job.title}
            </h2>
            <p className="text-base text-gray-600 dark:text-gray-300">
              {job.department} • Remote • Voluntary • <span className="text-purple-600 dark:text-purple-400">Equity</span>
            </p>
            <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              Step {currentStep + 1} of {totalSteps}
            </div>
          </div>

          {/* Status Messages */}
          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl flex items-center">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mr-3" />
              <span className="text-green-800 dark:text-green-300">
                Email draft opened! Please review and send it to complete your application.
              </span>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-3" />
              <span className="text-red-800 dark:text-red-300">
                Error: {errorMessage}
              </span>
            </div>
          )}

          {/* Step Content */}
          <form onSubmit={handleSubmit}>
            <div className="min-h-[400px] flex flex-col justify-center">
              {renderStepContent()}
            </div>

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t border-gray-200 dark:border-dark-600">
              <button
                type="button"
                onClick={prevStep}
                disabled={currentStep === 0}
                className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                  currentStep === 0
                    ? 'opacity-50 cursor-not-allowed text-gray-400'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 border border-gray-300 dark:border-dark-600'
                }`}
              >
                Back
              </button>
              
              <div className="flex gap-2">
                {Array.from({ length: totalSteps }, (_, i) => (
                  <div
                    key={i}
                    className={`w-2 h-2 rounded-full transition-all ${
                      i === currentStep
                        ? 'bg-primary-600 w-8'
                        : i < currentStep
                        ? 'bg-primary-400'
                        : 'bg-gray-300 dark:bg-dark-600'
                    }`}
                  />
                ))}
              </div>

              {currentStep === totalSteps - 1 ? (
                <button
                  type="submit"
                  disabled={isSubmitting || !canProceed()}
                  className="px-8 py-3 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-xl font-bold hover:from-primary-700 hover:to-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={nextStep}
                  disabled={!canProceed()}
                  className="px-8 py-3 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-xl font-bold hover:from-primary-700 hover:to-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
                >
                  {currentStep === 0 && !formData.full_name ? 'Start' : 'Continue'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export function JobApplicationForm({ job, isOpen, onClose }: JobApplicationFormProps) {
  return <JobApplicationFormContent job={job} isOpen={isOpen} onClose={onClose} />;
}
